import { useTranslation } from 'next-i18next';
import translationFallback from '@/utilites/translations/translationFallback';

interface BuyersGuideStructuredDataProps {
  canonicalUrl?: string;
}

export default function BuyersGuideStructuredData({
                                                    canonicalUrl = 'https://spiritory.com/buyers-guide'
                                                  }: BuyersGuideStructuredDataProps) {
  const { t } = useTranslation('buyers_guide');
  const { t: tCommon } = useTranslation('common');

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": canonicalUrl,
        "url": canonicalUrl,
        "name": translationFallback(
          'buyers_guide_title',
          tCommon('buyers_guide_title'),
          'How to Buy Whisky Safely – Buyers Guide | Spiritory'
        ),
        "description": translationFallback(
          'buyers_guide_description',
          tCommon('buyers_guide_description'),
          'A step-by-step guide to help whisky collectors and buyers purchase bottles securely on Spiritory marketplace.'
        ),
        "isPartOf": {
          "@id": "https://spiritory.com/#website"
        },
        "inLanguage": "en-US",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://spiritory.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Buyers Guide",
              "item": canonicalUrl
            }
          ]
        },
        "datePublished": "2024-09-15",
        "dateModified": "2025-07-17"
      },
      {
        "@type": "HowTo",
        "name": translationFallback(
          'how_to_buy_whisky_safely',
          t('how_to_buy_whisky_safely'),
          'How to Buy Whisky Safely on Spiritory'
        ),
        "description": translationFallback(
          'buyers_guide_description',
          t('buyers_guide_description'),
          'A step-by-step guide to help whisky collectors and buyers purchase bottles securely on Spiritory.'
        ),
        "image": {
          "@type": "ImageObject",
          "url": "https://spiritory.com/images/buy-guide/buyer-guide2.svg",
          "width": 800,
          "height": 600
        },
        "totalTime": "PT20M",
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": "EUR",
          "value": 0
        },
        "supply": [
          {
            "@type": "HowToSupply",
            "name": "Spiritory user account"
          },
          {
            "@type": "HowToSupply",
            "name": "Valid payment method"
          },
          {
            "@type": "HowToSupply",
            "name": "Delivery address"
          }
        ],
        "step": [
          {
            "@type": "HowToStep",
            "name": "Finding Your Whisky Bottle",
            "text": translationFallback(
              'step_finding_bottle',
              t('step_finding_bottle'),
              "Simply browse our growing marketplace and select the bottle you're looking to buy."
            ),
            "position": 1
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page",
            "text": translationFallback(
              'step_product_page',
              t('step_product_page'),
              "Once you've found your desired bottle, you can decide on how you want to buy it. Your two options are to either 'buy now' or 'place a bid'. We will explain the two options now."
            ),
            "position": 2
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page: Buy now",
            "text": translationFallback(
              'step_buy_now',
              t('step_buy_now'),
              "Buy instantly at the lowest 'Ask' (offer) of a seller"
            ),
            "position": 3
          },
          {
            "@type": "HowToStep",
            "name": "The Spiritory Product Page: Place a bid",
            "text": translationFallback(
              'step_place_bid',
              t('step_place_bid'),
              "Placing a 'bid', you decide the price you're buying at. If you have the highest bid, we will match you with a seller, willing to sell at that price. You can always adjust your bid, in case no seller is found at that price."
            ),
            "position": 4
          },
          {
            "@type": "HowToStep",
            "name": "Successfully purchase a whisky bottle at Spiritory",
            "text": translationFallback(
              'step_successful_purchase',
              t('step_successful_purchase'),
              "Great, now it's our turn! We make sure everything is safe and secure for our buyers. Find out about how we do that in our next step."
            ),
            "position": 5
          },
          {
            "@type": "HowToStep",
            "name": "Pay Safe with Spiritory",
            "text": translationFallback(
              'step_pay_safe',
              t('step_pay_safe'),
              "Only if the bottle successfully passes our authentication, you will be charged. That's how we ensure everything is okay with the bottle. You pay, the seller ships and is paid when you've confirmed the safe arrival."
            ),
            "position": 6
          }
        ],
        "datePublished": "2024-09-15",
        "dateModified": "2025-07-17"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_need_account_q',
              t('faq_need_account_q'),
              'Do I need an account to buy premium whisky on Spiritory marketplace?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_need_account_a',
                t('faq_need_account_a'),
                'Yes, in order to make a purchase or sell a bottle on Spiritory you have to register as a user. You can do so here, or at the top right of the page in "Sign-in".'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_which_countries_q',
              t('faq_which_countries_q'),
              "Which countries can buy whisky through Spiritory's international marketplace?"
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_which_countries_a',
                t('faq_which_countries_a'),
                'Spiritory is currently available in European Union countries.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_bidding_work_q',
              t('faq_bidding_work_q'),
              'How does whisky bidding work on Spiritory platform?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_bidding_work_a',
                t('faq_bidding_work_a'),
                'When bidding on a product, the buyer can suggest a price for the bottle, this offer can last from 7 to 60 days. This offer is automatically sent to the seller. The seller can accept or refuse this price and the user will be notified with his decision. All in a transparent way for buyers and sellers.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_how_to_buy_q',
              t('faq_how_to_buy_q'),
              'How do I buy a premium whisky bottle on Spiritory marketplace?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_how_to_buy_a',
                t('faq_how_to_buy_a'),
                'Buy Now: Click on the "Buy Now" button to accept the listed price. Once you confirm the purchase, the authentication process will begin, which typically takes between 1 to 48 hours. After the bottle is authenticated, you\'ll receive a notification and can proceed with the payment. Place a Bid: Click on "Place a Bid" to propose your own price for the bottle. The seller can either accept or reject your offer. If the seller accepts, the same process follows: authentication of the bottle and then payment. Both methods ensure transparency for both buyers and sellers.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_when_pay_q',
              t('faq_when_pay_q'),
              'When do I pay after winning a whisky bid or buying a bottle?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_when_pay_a',
                t('faq_when_pay_a'),
                'After placing an order, the seller must start the authentication process of the bottle, which can take from 1 to 48 hours, after passing the verification by our team, you will be informed that you can proceed with the payment.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_get_invoice_q',
              t('faq_get_invoice_q'),
              'How do I get an invoice for my premium whisky purchase?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_get_invoice_a',
                t('faq_get_invoice_a'),
                'The Invoice can be found in the Offer Status in the Dashboard and can be downloaded after the payment was successful!'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_additional_taxes_q',
              t('faq_additional_taxes_q'),
              'Are there additional taxes when buying whisky on Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_additional_taxes_a',
                t('faq_additional_taxes_a'),
                'No, Spiritory only includes the fees established for the seller and the buyer as a marketplace.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_discount_codes_q',
              t('faq_discount_codes_q'),
              'How do I use discount codes when buying whisky on Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_discount_codes_a',
                t('faq_discount_codes_a'),
                'A discount coupon can be applied in the payment screen. There you have an extra field where you can input your coupon code.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_cancel_order_q',
              t('faq_cancel_order_q'),
              'Can I cancel my whisky order and get a refund on Spiritory?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_cancel_order_a',
                t('faq_cancel_order_a'),
                'Refunds are available if the return request meets our policies for unopened bottles from commercial sellers. Refunds are not granted for private seller transactions or opened bottles.'
              )
            }
          },
          {
            "@type": "Question",
            "name": translationFallback(
              'faq_buyer_protection_q',
              t('faq_buyer_protection_q'),
              'What is the Spiritory buyer protection?'
            ),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": translationFallback(
                'faq_buyer_protection_a',
                t('faq_buyer_protection_a'),
                'Payment on Spiritory is absolutely secure: we hold the purchase amount in our deposit account for up to 14 days. Your money is safe until you receive the bottles.'
              )
            }
          }
        ]
      },
      {
        "@type": "Organization",
        "@id": "https://spiritory.com/#organization",
        "name": "Spiritory",
        "url": "https://spiritory.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://spiritory.com/images/logo.png",
          "width": 200,
          "height": 60
        },
        "sameAs": [
          "https://www.facebook.com/spiritory",
          "https://twitter.com/spiritory",
          "https://www.instagram.com/spiritory"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+34-XXX-XXX-XXX",
          "contactType": "customer service",
          "areaServed": "ES",
          "availableLanguage": [
            "Spanish", "English", "French", "Italian", "German"
          ]
        }
      }
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(structuredData)
      }}
    />
  );
}